import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import TextInput from '__components/Inputs/TextInput';
import LongText from '__components/Inputs/LongText';
import Loader from '__components/Kizen/Loader';
import Switch from '__components/Kizen/Switch';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  mcpServerListQuery,
  mcpServerQuery,
  mcpServerStepOptionsQuery,
} from 'query/mcp';
import MCPServerService from 'services/MCPServerService';
import { SettingsContentCard } from '__components/Card/Card';
import { LinkText, Typography } from '@kizen/kds/Typography';
import { SubSectionWithHeader as SubSection } from 'components/Layout/SubSection';
import Cols from '__components/Layout/Cols';
import InputControl from '__components/Inputs/InputControl';
import { useSetSaveAction } from '../ToolbarContext';
import { useMCPServerDetailPage } from '../context';
import {
  StyledDetails,
  StyledConnectSteps,
} from 'pages/MCPServerSettings/styles';
import { MCP_DEVELOPER_DOCS_URL } from 'pages/MCPServerSettings/constants';
import type { ReactNode } from 'react';

const StyledSettingsContentCard = styled(SettingsContentCard)`
  margin: 15px auto 20px;
`;

const StyledConnectSection = styled.div`
  margin-top: 20px;
  text-align: start;
`;

const StyledProductHeader = styled(Typography)`
  display: block;
  margin-bottom: 10px;
`;

const ProductBlock = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <InputControl margin>
    <StyledProductHeader variant="header" size="lg">
      {title}
    </StyledProductHeader>
    {children}
  </InputControl>
);

export const MCPServerSettings = () => {
  const { t } = useTranslation();
  const { mcpServerId } = useParams<{ mcpServerId: string }>();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();
  const setSaveAction = useSetSaveAction();

  const { canWrite, isSystemManaged } = useMCPServerDetailPage();

  const queryOptions = useMemo(
    () =>
      mcpServerQuery(mcpServerId, {
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!mcpServerId,
      }),
    [mcpServerId]
  );

  const { data: server, isLoading } = useQuery(queryOptions);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  const prevServerId = useRef<string | null>(null);
  if (server && prevServerId.current !== server.id) {
    setName(server.name);
    setDescription(server.description ?? '');
    setIsEnabled(server.is_enabled ?? true);
    prevServerId.current = server.id;
  }

  const { mutate: saveServer, isPending: isSaving } = useMutation({
    mutationFn: () =>
      MCPServerService.updateMCPServer(mcpServerId, {
        name: isSystemManaged ? undefined : name.trim(),
        description: description.trim(),
        is_enabled: isEnabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      queryClient.invalidateQueries({
        queryKey: mcpServerListQuery().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: mcpServerStepOptionsQuery().queryKey,
      });
      showToast({
        message: t('MCP server saved successfully'),
        variant: toastVariant.SUCCESS,
      });
    },
    onError: (error) => {
      if (server) {
        setName(server.name);
        setDescription(server.description ?? '');
        setIsEnabled(server.is_enabled ?? true);
      }
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to save MCP server'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const handleSave = useCallback(() => {
    if (!name.trim() && !isSystemManaged) {
      setNameError(t('Please name your MCP server.'));
      return;
    }
    setNameError('');
    saveServer();
  }, [name, isSystemManaged, saveServer, t]);

  const nameChanged = !isSystemManaged && name !== server?.name;
  const descriptionChanged = description !== (server?.description ?? '');
  const enabledChanged = isEnabled !== (server?.is_enabled ?? true);
  const isDirty =
    server && (nameChanged || descriptionChanged || enabledChanged);

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!canWrite) {
      setSaveAction(null);
      return () => setSaveAction(null);
    }
    setSaveAction({
      onSave: () => handleSaveRef.current(),
      disabled: !isDirty || isSaving,
    });
    return () => setSaveAction(null);
  }, [canWrite, isDirty, isSaving, setSaveAction]);

  return (
    <Loader loading={isLoading}>
      <StyledSettingsContentCard>
        <SubSection title={t('MCP Server Settings')} action={null}>
          <Cols columns={2} gutter="20px">
            <InputControl margin>
              <TextInput
                label={t('Name')}
                value={name}
                onChange={setName}
                disabled={!canWrite || isSystemManaged}
                error={nameError || undefined}
                data-qa-id="mcp-server-settings-name-input"
              />
            </InputControl>
            <InputControl margin>
              <TextInput
                label={t('API Name')}
                value={server?.api_name ?? ''}
                disabled
                data-qa-id="mcp-server-settings-api-name-input"
              />
            </InputControl>
            <InputControl margin label={t('Enabled')}>
              <Switch
                checked={isEnabled}
                onChange={() => setIsEnabled((prev) => !prev)}
                disabled={!canWrite}
                removeMargin
                dataQa="mcp-server-settings-enabled-toggle"
              />
            </InputControl>
          </Cols>
          <InputControl margin>
            <LongText
              label={t('Description')}
              placeholder={t('Enter Description')}
              value={description}
              onChange={setDescription}
              disabled={!canWrite}
              data-qa-id="mcp-server-settings-description-input"
            />
          </InputControl>
        </SubSection>

        {server ? (
          <StyledConnectSection>
            <SubSection
              title={t('Connecting to this MCP Server')}
              action={null}
            >
              <InputControl margin label={t('Endpoint URL')}>
                <StyledDetails>{server.endpoint_url}</StyledDetails>
              </InputControl>
              <ProductBlock title={t('MCP Inspector')}>
                <StyledDetails>
                  {`npx @modelcontextprotocol/inspector ${server.endpoint_url}`}
                </StyledDetails>
              </ProductBlock>
              <ProductBlock title={t('Claude (chat app)')}>
                <StyledConnectSteps>
                  <li>{t('Settings, Connectors, Add custom connector')}</li>
                  <li>{t('Paste the endpoint URL')}</li>
                  <li>{t('Click Add, then Connect, then finish sign in')}</li>
                </StyledConnectSteps>
              </ProductBlock>
              <ProductBlock title={t('Claude Code')}>
                <StyledDetails>
                  {`claude mcp add --transport http kizen-${
                    server.api_name ?? 'mcp'
                  } ${server.endpoint_url} --scope user`}
                </StyledDetails>
              </ProductBlock>
              <ProductBlock title={t('ChatGPT (Codex)')}>
                <StyledConnectSteps>
                  <li>{t('Settings, Integrations & MCP, Add server')}</li>
                  <li>{t('Name it, choose Streamable HTTP')}</li>
                  <li>{t('Paste the endpoint URL, finish sign in')}</li>
                </StyledConnectSteps>
              </ProductBlock>
              <ProductBlock title={t('Antigravity')}>
                <StyledDetails>
                  {JSON.stringify(
                    {
                      mcpServers: {
                        [`kizen-${server.api_name ?? 'mcp'}`]: {
                          serverUrl: server.endpoint_url,
                        },
                      },
                    },
                    null,
                    2
                  )}
                </StyledDetails>
              </ProductBlock>
              <InputControl margin>
                <LinkText
                  href={MCP_DEVELOPER_DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('Full setup and troubleshooting: Connect an AI Assistant')}
                </LinkText>
              </InputControl>
            </SubSection>
          </StyledConnectSection>
        ) : null}
      </StyledSettingsContentCard>
    </Loader>
  );
};
